import type { FormSubmissionDto } from "../../types/submission";
import type {
  MarksSummary,
  StaffColumnDto,
  StaffColumnOptions,
  StaffColumnType,
  StaffMarkFilters,
  StaffMarkValue,
  StaffMarksMap,
} from "../../types/staffColumn";

export const STAFF_COLUMNS_LIMIT = 10;
export const STAFF_TEXT_MAX_LENGTH = 2000;
export const STAFF_COLUMN_KEY_PREFIX = "staff:";

export const DEFAULT_STATUS_CHOICES = ["новый", "в работе", "готово"];

export const STAFF_COLUMN_TYPE_OPTIONS: Array<{
  type: StaffColumnType;
  name: string;
  label: string;
  hint: string;
  options?: StaffColumnOptions;
}> = [
  {
    type: "checkbox",
    name: "Отмечено",
    label: "Галочка",
    hint: "Да / нет по строке",
  },
  {
    type: "status",
    name: "Статус",
    label: "Статус",
    hint: "Выбрать из списка",
    options: { choices: [...DEFAULT_STATUS_CHOICES] },
  },
  {
    type: "text",
    name: "Комментарий",
    label: "Заметка",
    hint: "Свободный текст",
  },
  {
    type: "number",
    name: "Число",
    label: "Число",
    hint: "Можно суммировать",
  },
];

export const DEFAULT_STAFF_COLUMN_PRESETS: Array<{
  name: string;
  type: StaffColumnType;
  options?: StaffColumnOptions;
}> = STAFF_COLUMN_TYPE_OPTIONS.filter(
  (item) => item.type === "status" || item.type === "text",
).map(({ name, type, options }) => ({ name, type, options }));

const COLUMN_TYPES = new Set<StaffColumnType>([
  "checkbox",
  "status",
  "text",
  "number",
]);

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function readId(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
}

export function isStaffColumnType(value: unknown): value is StaffColumnType {
  return (
    typeof value === "string" && COLUMN_TYPES.has(value as StaffColumnType)
  );
}

export function staffColumnTypeLabel(type: StaffColumnType): string {
  return (
    STAFF_COLUMN_TYPE_OPTIONS.find((item) => item.type === type)?.label ?? type
  );
}

export function staffColumnTypeHint(type: StaffColumnType): string {
  return (
    STAFF_COLUMN_TYPE_OPTIONS.find((item) => item.type === type)?.hint ?? ""
  );
}

export function uniqueStaffColumnName(
  columns: Array<{ name: string }>,
  base: string,
): string {
  const trimmed = base.trim() || "Пометка";
  const names = new Set(
    columns.map((column) => column.name.trim().toLowerCase()),
  );
  if (!names.has(trimmed.toLowerCase())) return trimmed;
  let index = 2;
  while (names.has(`${trimmed} ${index}`.toLowerCase())) {
    index += 1;
  }
  return `${trimmed} ${index}`;
}

export function staffColumnKey(columnId: number): string {
  return `${STAFF_COLUMN_KEY_PREFIX}${columnId}`;
}

export function parseStaffColumnKey(key: string): number | null {
  if (!key.startsWith(STAFF_COLUMN_KEY_PREFIX)) return null;
  return readId(key.slice(STAFF_COLUMN_KEY_PREFIX.length));
}

export function staffColumnExportLabel(column: StaffColumnDto): string {
  return column.label?.trim() || `Пометка: ${column.name}`;
}

function normalizeChoices(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const choices: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const value = readString(item);
    if (!value || seen.has(value)) continue;
    seen.add(value);
    choices.push(value);
  }
  return choices;
}

export function normalizeStaffColumn(raw: unknown): StaffColumnDto | null {
  if (!raw || typeof raw !== "object") return null;
  const item = raw as Record<string, unknown>;
  const id = readId(item.id);
  const name = readString(item.name);
  const type = item.type;
  if (id == null || !name || !isStaffColumnType(type)) return null;

  const optionsRaw = item.options;
  const choices = normalizeChoices(
    optionsRaw && typeof optionsRaw === "object"
      ? (optionsRaw as { choices?: unknown }).choices
      : null,
  );

  return {
    id,
    name,
    type,
    options: type === "status" ? { choices } : null,
    order: readId(item.order) ?? 0,
    createdAt: readString(item.createdAt ?? item.created_at),
    updatedAt: readString(item.updatedAt ?? item.updated_at),
    label: readString(item.label) ?? undefined,
  };
}

export function normalizeStaffColumns(raw: unknown): StaffColumnDto[] {
  const rows = Array.isArray(raw)
    ? raw
    : raw &&
        typeof raw === "object" &&
        Array.isArray((raw as { data?: unknown }).data)
      ? (raw as { data: unknown[] }).data
      : raw &&
          typeof raw === "object" &&
          Array.isArray((raw as { staffColumns?: unknown }).staffColumns)
        ? (raw as { staffColumns: unknown[] }).staffColumns
        : [];

  return rows
    .map(normalizeStaffColumn)
    .filter((item): item is StaffColumnDto => item != null)
    .sort((left, right) => left.order - right.order || left.id - right.id);
}

export function normalizeStaffMarkValue(
  type: StaffColumnType | undefined,
  raw: unknown,
): StaffMarkValue | undefined {
  if (raw == null) return undefined;
  if (type === "checkbox") {
    if (raw === true || raw === "true" || raw === 1 || raw === "1") return true;
    if (raw === false || raw === "false" || raw === 0 || raw === "0") {
      return false;
    }
    return undefined;
  }
  if (type === "number") {
    const numeric = Number(raw);
    return Number.isFinite(numeric) ? numeric : undefined;
  }
  if (type === "status" || type === "text") {
    if (typeof raw === "string") return raw;
    if (typeof raw === "number" || typeof raw === "boolean") return String(raw);
    return undefined;
  }
  if (
    typeof raw === "boolean" ||
    typeof raw === "number" ||
    typeof raw === "string"
  ) {
    return raw;
  }
  return undefined;
}

export function normalizeStaffMarks(
  raw: unknown,
  columns?: StaffColumnDto[],
): StaffMarksMap {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const types = new Map(
    (columns ?? []).map((column) => [String(column.id), column.type]),
  );
  const marks: StaffMarksMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const id = readId(key);
    if (id == null || value == null) continue;
    const normalized = normalizeStaffMarkValue(types.get(String(id)), value);
    if (normalized === undefined) continue;
    marks[String(id)] = normalized;
  }
  return marks;
}

export function getStaffMark(
  submission: FormSubmissionDto,
  column: StaffColumnDto,
): StaffMarkValue | undefined {
  const marks = submission.marks ?? {};
  return normalizeStaffMarkValue(column.type, marks[String(column.id)]);
}

export function submissionMatchesStaffFilters(
  submission: FormSubmissionDto,
  columns: StaffColumnDto[],
  filters: StaffMarkFilters,
): boolean {
  for (const column of columns) {
    const expected = filters[String(column.id)];
    if (expected == null || expected === "") continue;
    const actual = getStaffMark(submission, column);

    if (column.type === "checkbox") {
      if (expected === "true") {
        if (actual !== true) return false;
      } else if (expected === "false") {
        if (actual === true) return false;
      }
      continue;
    }

    const actualText =
      actual == null
        ? ""
        : typeof actual === "string"
          ? actual
          : String(actual);
    if (actualText !== expected) return false;
  }
  return true;
}

export function filterSubmissionsByStaffMarks(
  submissions: FormSubmissionDto[],
  columns: StaffColumnDto[],
  filters: StaffMarkFilters,
): FormSubmissionDto[] {
  const active = Object.values(filters).some((value) => value !== "");
  if (!active) return submissions;
  return submissions.filter((item) =>
    submissionMatchesStaffFilters(item, columns, filters),
  );
}

export function computeMarksSummary(
  submissions: FormSubmissionDto[],
  columns: StaffColumnDto[],
): MarksSummary {
  const summary: MarksSummary = {};
  for (const column of columns) {
    if (column.type !== "number") continue;
    let sum = 0;
    let count = 0;
    for (const submission of submissions) {
      const value = getStaffMark(submission, column);
      if (typeof value !== "number" || !Number.isFinite(value)) continue;
      sum += value;
      count += 1;
    }
    if (count === 0) continue;
    summary[String(column.id)] = {
      sum,
      avg: sum / count,
      count,
    };
  }
  return summary;
}

export function formatMarksSummaryValue(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return value.toLocaleString("ru-RU", { maximumFractionDigits: 2 });
}

export function parseStaffTextValue(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, STAFF_TEXT_MAX_LENGTH);
}

export function parseStaffNumberValue(raw: string): number | null {
  const trimmed = raw.trim().replace(",", ".");
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? numeric : null;
}

export function mergeSubmissionMarks(
  current: StaffMarksMap | undefined,
  patch: Record<string, StaffMarkValue | null>,
): StaffMarksMap {
  const next = { ...(current ?? {}) };
  for (const [key, value] of Object.entries(patch)) {
    if (value == null) {
      delete next[key];
    } else {
      next[key] = value;
    }
  }
  return next;
}
